import type { ChatConversation } from "@/lib/chatApi";

export type ChatSidebarKind = "dm" | "group" | "room";

export type ChatSidebarOrder = {
  pinned: number[];
};

const STORAGE_KEY = "chat_sidebar_pins_v1";

function emptyOrder(): ChatSidebarOrder {
  return { pinned: [] };
}

export function chatSidebarKind(c: { type?: string; is_room?: boolean }): ChatSidebarKind {
  if (c.type === "dm") return "dm";
  if (c.is_room) return "room";
  return "group";
}

function sanitizeIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const out: number[] = [];
  const seen = new Set<number>();
  for (const v of raw) {
    const id = Number(v);
    if (!Number.isFinite(id) || id < 1 || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export function parseChatSidebarOrder(raw: unknown): ChatSidebarOrder {
  if (!raw || typeof raw !== "object") return emptyOrder();
  const rec = raw as Record<string, unknown>;
  return { pinned: sanitizeIds(rec.pinned) };
}

export function isChatSidebarOrderEmpty(order: ChatSidebarOrder): boolean {
  return order.pinned.length === 0;
}

export function readChatSidebarOrder(userId: number): ChatSidebarOrder {
  if (userId < 1) return emptyOrder();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyOrder();
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const entry = parsed[String(userId)];
    if (!entry || typeof entry !== "object") return emptyOrder();
    return parseChatSidebarOrder(entry);
  } catch {
    return emptyOrder();
  }
}

export function writeChatSidebarOrder(userId: number, order: ChatSidebarOrder): void {
  if (userId < 1) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const map = parsed && typeof parsed === "object" ? parsed : {};
    map[String(userId)] = { pinned: sanitizeIds(order.pinned) };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* quota / mode privé */
  }
}

function recencyKey(c: ChatConversation): string {
  return String(c.last_message?.created_at ?? c.updated_at ?? "");
}

function byRecency(a: ChatConversation, b: ChatConversation): number {
  return recencyKey(b).localeCompare(recencyKey(a));
}

export function applyChatSidebarOrder(
  items: ChatConversation[],
  order: ChatSidebarOrder
): ChatConversation[] {
  const pinnedSet = new Set(order.pinned);
  const byId = new Map(items.map((c) => [c.id, c]));
  const pinned: ChatConversation[] = [];
  for (const id of order.pinned) {
    const c = byId.get(id);
    if (c) pinned.push(c);
  }
  const rest = items.filter((c) => !pinnedSet.has(c.id)).sort(byRecency);
  return [...pinned, ...rest];
}

export function toggleChatSidebarPin(order: ChatSidebarOrder, id: number): ChatSidebarOrder {
  const cid = Number(id);
  if (!Number.isFinite(cid) || cid < 1) return order;
  if (order.pinned.includes(cid)) {
    return { pinned: order.pinned.filter((x) => x !== cid) };
  }
  return { pinned: [cid, ...order.pinned.filter((x) => x !== cid)] };
}
